import { useState, useEffect } from 'react';
import { AnimatePresence, motion } from "framer-motion";
import Questions from '../components/Questions';
import { convertKgToLbs, convertLbsToKg } from '../utils/utils';

const Weight = ({
  subGroup,
  validationErrors,
  setValidationErrors,
  inputRefs,
  handleInputBlur,
  setQuestions,
  firstLevelArea,
  secondLevelArea,
  subArea,
  inputBlurTriggered,
  savingQuestionId,
  fetchAreaNames,
  client,
  product,
  language,
  setIsDataSaved,
}) => {
  const [weightValues, setWeightValues] = useState({
    kg: '',
    lbs: '',
  });

  const [isKgSelected, setIsKgSelected] = useState(true); // To manage toggle state for Kg or Lbs

  // Identify the weight-related questions
  const kgQuestion = subGroup.subAreaCompositionDtoList.find(q => q.code === 'Q_WEIGHT');
  const lbsQuestion = subGroup.subAreaCompositionDtoList.find(q => q.code === 'Q_WEIGHT_POUNDS');
  const unitToggleQuestion = subGroup.subAreaCompositionDtoList.find(q => q.uiRenderType === 'WEIGHT_UNIT');

  useEffect(() => {
    if (kgQuestion && lbsQuestion && unitToggleQuestion) {
      // Set the initial toggle state based on the unit question
      setIsKgSelected(unitToggleQuestion.value === unitToggleQuestion.possibleValues[0].value);

      setWeightValues({
        kg: kgQuestion.value || '',
        lbs: lbsQuestion.value || '',
      });
    }
  }, [kgQuestion, lbsQuestion, unitToggleQuestion]);

  const handleWeightChange = async (updatedField, value) => {
    let updatedValues = { ...weightValues };

    const numericValue = value === '' || value === null ? '' : parseInt(value, 10);

    if (updatedField === 'kg') {
      updatedValues.kg = numericValue;
      if (numericValue === '' || numericValue === null) {
        updatedValues.lbs = null; // Set lbs to null if kg is null
      } else {
        updatedValues.lbs = convertKgToLbs(numericValue);
      }
    } else if (updatedField === 'lbs') {
      updatedValues.lbs = numericValue;
      if (numericValue === '' || numericValue === null) {
        updatedValues.kg = null; // Set kg to null if lbs is null
      } else {
        updatedValues.kg = convertLbsToKg(numericValue);
      }
    }

    setWeightValues(updatedValues);

    const updatedQuestions = [
      { ...kgQuestion, value: updatedValues.kg === '' ? null : updatedValues.kg },
      { ...lbsQuestion, value: updatedValues.lbs === '' ? null : updatedValues.lbs },
    ];

    await handleInputBlur(updatedQuestions);
  };


  const handleToggleChange = async (updatedQuestion) => {
    const newValue = updatedQuestion.value;

    setTimeout(() => {
      setIsKgSelected(newValue === unitToggleQuestion.possibleValues[0].value); // Toggle between Kg and Lbs
    }, 100);

    await handleInputBlur(updatedQuestion); // Save the toggle state
  };

  return (
    <div className={subGroup.uiRenderType}>
      <div className="questions">
      <AnimatePresence mode="popLayout">
        {subGroup.subAreaCompositionDtoList.map((question) => {
          const shouldDisplay =
            (isKgSelected && question.code === 'Q_WEIGHT') ||
            (!isKgSelected && question.code === 'Q_WEIGHT_POUNDS') ||
            ["WEIGHT_UNIT"].includes(question.uiRenderType);

          return (
            <motion.div
              key={`question-${question.uniqueId}`}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className={
                shouldDisplay
                  ? question.code // show normally
                  : 'd-none' // hidden if not applicable
              }
            >
              <div>
                {isKgSelected && question.code === 'Q_WEIGHT' && (
                  <Questions
                    key={question.uniqueId}
                    question={question}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    inputRefs={inputRefs}
                    setQuestions={setQuestions}
                    handleInputBlur={async (updatedQuestion) => {
                      const value = updatedQuestion.value;
                      await handleWeightChange('kg', value);
                    }}
                    firstLevelArea={firstLevelArea}
                    subArea={subArea}
                    secondLevelArea={secondLevelArea}
                    inputBlurTriggered={inputBlurTriggered}
                    savingQuestionId={savingQuestionId}
                    fetchAreaNames={fetchAreaNames}
                    client={client}
                    product={product}
                    language={language}
                    setIsDataSaved={setIsDataSaved}
                  />
                )}
              </div>

              <div>
                {!isKgSelected && question.code === 'Q_WEIGHT_POUNDS' && (
                  <Questions
                    key={question.uniqueId}
                    question={question}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    inputRefs={inputRefs}
                    setQuestions={setQuestions}
                    handleInputBlur={async (updatedQuestion) => {
                      const value = updatedQuestion.value;
                      await handleWeightChange('lbs', value);
                    }}
                    firstLevelArea={firstLevelArea}
                    subArea={subArea}
                    secondLevelArea={secondLevelArea}
                    inputBlurTriggered={inputBlurTriggered}
                    savingQuestionId={savingQuestionId}
                    fetchAreaNames={fetchAreaNames}
                    client={client}
                    product={product}
                    language={language}
                    setIsDataSaved={setIsDataSaved}
                  />
                )}
              </div>

              <div>
                {["WEIGHT_UNIT"].includes(question.uiRenderType) && (
                  <Questions
                    key={question.uniqueId}
                    question={question}
                    validationErrors={validationErrors}
                    setValidationErrors={setValidationErrors}
                    inputRefs={inputRefs}
                    setQuestions={setQuestions}
                    handleInputBlur={async (updatedQuestion) => {
                      await handleToggleChange(updatedQuestion);
                    }}
                    firstLevelArea={firstLevelArea}
                    subArea={subArea}
                    secondLevelArea={secondLevelArea}
                    inputBlurTriggered={inputBlurTriggered}
                    savingQuestionId={savingQuestionId}
                    fetchAreaNames={fetchAreaNames}
                    client={client}
                    product={product}
                    language={language}
                    setIsDataSaved={setIsDataSaved}
                  />
                )}
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default Weight;
